import {
    Grid2 as Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useState, ChangeEvent, Dispatch, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PasswordChecklist from "react-password-checklist";
import passwordUtils, { SPECIAL_CHARACTER_REGEX } from '../../utils/passwordUtils';
import HeaderForgotPassword from './HeaderForgotPassword';

const MIN_LENGTH = 8;

interface PasswordResetProps {
    password: string;
    setPassword: Dispatch<SetStateAction<string>>;
    confirmPassword: string;
    setConfirmPassword: Dispatch<SetStateAction<string>>;
    setIsValidPasswordForm: Dispatch<SetStateAction<boolean>>;
}

const PasswordReset = ({
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    setIsValidPasswordForm,
}: PasswordResetProps) => {
    const { t } = useTranslation();
    const [isShowPassword, setIsShowPassword] = useState(false);
    const [matchPassword, setMatchPassword] = useState("");

    const onSetMatchPassword = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setMatchPassword(event.target.value);
    };

    const onClickShowPassword = () => setIsShowPassword((show) => !show);


    useEffect(() => {
        if (password === "" || matchPassword === "") return;
        setIsValidPasswordForm(passwordUtils.isPasswordValid(password, matchPassword));
    }, [password, matchPassword]);

    return (
        <>
            <HeaderForgotPassword title={t('passwordReset.newPassword')}>
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    size="medium"
                    id="new-password"
                    autoComplete="off"
                    variant="outlined"
                    sx={{
                        background: "white"
                    }}
                    label={t('passwordReset.newPassword')}
                    type={isShowPassword ? 'text' : 'password'}
                    onChange={(e) => setPassword(e.target.value)}
                    value={password}
                    slotProps={{
                        input: {
                            startAdornment: <InputAdornment position="start"><IconButton></IconButton>{<KeyIcon sx={{ fontSize: 15 }} />}</InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={onClickShowPassword}
                                        edge="end"
                                    >
                                        {isShowPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }
                    }}
                />

                <TextField
                    margin="normal"
                    required
                    fullWidth
                    size="medium"
                    id="confirm-password"
                    autoComplete="off"
                    variant="outlined"
                    sx={{
                        background: "white"
                    }}
                    label={t('passwordReset.confirmNewPassword')}
                    type={isShowPassword ? 'text' : 'password'}
                    onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        onSetMatchPassword(e);
                    }}
                    value={confirmPassword}
                    slotProps={{
                        input: {
                            startAdornment: <InputAdornment position="start"><IconButton></IconButton>{<KeyIcon sx={{ fontSize: 15 }} />}</InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={onClickShowPassword}
                                        edge="end"
                                    >
                                        {isShowPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }
                    }}
                />
                <Typography align="center" sx={{ mt: 1, fontSize: 13 }}>
                    {t("passwordReset.enterPasswordMessage")}
                </Typography>
                <Grid container sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                }}>
                    <Grid>
                        <PasswordChecklist
                            style={{ fontSize: 12 }}
                            rules={["lowercase", "capital", "specialChar", "number", "minLength", "match"]}
                            minLength={MIN_LENGTH}
                            value={password}
                            valueAgain={matchPassword}
                            specialCharsRegex={SPECIAL_CHARACTER_REGEX}
                            messages={{
                                minLength: t('passwordReset.minLength'),
                                specialChar: t('passwordReset.specialChar'),
                                number: t('passwordReset.number'),
                                capital: t('passwordReset.capital'),
                                match: t('passwordReset.match'),
                                lowercase: t('passwordReset.lowercase'),
                            }}
                        />
                    </Grid>
                </Grid>
            </HeaderForgotPassword>
        </>
    );
};

export default PasswordReset;
